import {
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Divider,
  Tooltip,
  Autocomplete,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import WorkIcon from "@mui/icons-material/Work";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { styled } from "@mui/system";
import api from '../config/api';
import CandidateSearchForm from "./CandidateSearchForm";
import PositionsModal from "../positions/PositionsModal";

const Input = styled("input")({ display: "none" });
const DragDropArea = styled(Paper)(({ theme, isdragging }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  border: "2px dashed #ccc",
  backgroundColor: isdragging === "true" ? "#f0f0f0" : "#fff",
  cursor: "pointer",
  borderRadius: theme.shape.borderRadius * 2,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: "180px",
}));

const PositionCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.primary.light + "10",
  border: `1px solid ${theme.palette.primary.light}`,
}));

// Filtro explícito del buscador de cargos: matchea SOLO por nombre del cargo,
// ignorando mayúsculas y tildes. Recibe el término desde el estado del
// componente (no desde el inputValue de MUI, ver positionSearchText).
const normalizeText = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const filterPositionsByName = (options, searchTerm) => {
  const term = normalizeText(searchTerm).trim();
  if (!term) return options;
  return options.filter((option) => normalizeText(option.name).includes(term));
};

// Animación CSS para el spinner
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Insertar los estilos en el documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = spinnerStyles;
  document.head.appendChild(styleSheet);
}

export default function MakerStepper({
  pasos,
  dynamicSteps,
  pasoFinal,
  positions,
  loadingPositions,
  selectedPosition,
  onPositionChange,
  candidateData,
  onCandidateChange,
  contexts,
  onContextChange,
  localFiles,
  onFilesChange,
  skipped,
  onSkippedChange,
  activeReport,
  gptTask,
  gptProcessing,
  sending,
  uploadedCount,
  totalFiles,
  progressPercent,
  allDone,
  t,
  authToken,
  user,
  project,
  organization,
  language,
  reportId,
  navigate,
  showAlert,
  onCreateReport,
  onProcessGptTask,
  onRetryGptTask,
  isInFinalState,
  downloadFile,
  downloadWordDocument,
  handleDynamicStepsChange,
  onRefreshPositions, // Nueva función para recargar positions
  isReportCompleted = false,
}) {
  // Estados del stepper
  const [activeStep, setActiveStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState(new Set());
  const [draggingStep, setDraggingStep] = useState(null);

  // Estados para configuración de pasos dinámicos
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [stepConfig, setStepConfig] = useState({
    title: "",
    description: "",
    gptInstructions: ""
  });

  // Estados para el modal de crear cargo
  const [openPositionsModal, setOpenPositionsModal] = useState(false);
  const [refreshPositions, setRefreshPositions] = useState(0);

  // Texto visible del buscador de cargos. Se usa para filtrar SIEMPRE por lo
  // que se ve en el campo: MUI ignora el filtro cuando el texto coincide con
  // el nombre del cargo seleccionado (le pasa '' al filtro y muestra todo),
  // por lo que no se puede confiar en el inputValue que entrega filterOptions.
  const [positionSearchText, setPositionSearchText] = useState('');

  // Estados para disc
  const [availableDiscTest, setAvailableDiscTest] = useState(null);
  const [loadingDiscTest, setLoadingDiscTest] = useState(false);
  const [availableReasoningTests, setAvailableReasoningTests] = useState([]);
  const [loadingReasoningTests, setLoadingReasoningTests] = useState(false);

  // Estados para entrevista
  const [availableInterview, setAvailableInterview] = useState(null);
  const [loadingInterview, setLoadingInterview] = useState(false);

  // Estados para CV del candidato
  const [availableCv, setAvailableCv] = useState(null);
  const [loadingCv, setLoadingCv] = useState(false);

  const SELECTED_NARRATIVE_MAX_LENGTH = 500;

  const [availableNarratives, setAvailableNarratives] = useState([]);
  const [loadingNarratives, setLoadingNarratives] = useState(false);
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [openNarrativeDialog, setOpenNarrativeDialog] = useState(false);
  const [selectedNarrativeForm, setSelectedNarrativeForm] = useState({
    name: "",
    description: "",
    content: "",
  });

  const selectedNarrativeHasContent =
    !!selectedNarrative?.id && !!selectedNarrative?.content?.trim();

  // Combinar todos los pasos: pasos base + pasos dinámicos + paso final
  const allSteps = [...pasos, ...dynamicSteps, pasoFinal];
  const totalStepsWithoutFinal = pasos.length + dynamicSteps.length;

  // Función para validar email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función para verificar si un paso está completo
  const isStepComplete = useCallback((stepIndex) => {
    // Si el reporte está completado, mostrar todos los pasos como completos
    if (isInFinalState(activeReport?.status)) {
      // Paso final (generación de informe)
      if (stepIndex === totalStepsWithoutFinal) {
        return activeReport?.report_url ||
          activeReport?.word_document_url ||
          gptTask?.result?.reportHtmlUrl ||
          gptTask?.result?.reportWordUrl;
      }

      // Para los demás pasos, verificar si tienen contenido
      if (stepIndex === 0) {
        const hasCandidateData =
          candidateData.applicant !== null ||
          (candidateData.name.trim() !== "" &&
            candidateData.email.trim() !== "" &&
            isValidEmail(candidateData.email.trim()));

        const hasFilesOrContext =
          localFiles[0]?.length > 0 ||
          (contexts[0] && contexts[0].trim() !== "") ||
          skipped[stepIndex];

        return hasCandidateData && hasFilesOrContext;
      }

      if (stepIndex === 1) {
        return selectedPosition !== null;
      }
      return (
        localFiles[stepIndex]?.length > 0 ||
        (contexts[stepIndex] && contexts[stepIndex].trim() !== "") ||
        skipped[stepIndex]
      );
    }

    // Si el reporte NO está completado, validar las condiciones reales
    if (stepIndex === 0) {
      const hasCandidateData =
        candidateData.applicant !== null ||
        (candidateData.name.trim() !== "" &&
          candidateData.email.trim() !== "" &&
          isValidEmail(candidateData.email.trim()));

      const hasFilesOrContext =
        localFiles[0]?.length > 0 ||
        (contexts[0] && contexts[0].trim() !== "") ||
        skipped[stepIndex];

      return hasCandidateData && hasFilesOrContext && visitedSteps.has(stepIndex);
    }

    if (stepIndex === 1) {
      return selectedPosition !== null && visitedSteps.has(stepIndex);
    }

    // Para otros pasos
    const hasContent =
      localFiles[stepIndex]?.length > 0 ||
      (contexts[stepIndex] && contexts[stepIndex].trim() !== "") ||
      skipped[stepIndex];

    return hasContent && visitedSteps.has(stepIndex);
  }, [
    isInFinalState,
    activeReport,
    gptTask,
    totalStepsWithoutFinal,
    candidateData,
    localFiles,
    contexts,
    skipped,
    selectedPosition,
    visitedSteps
  ]);

  useEffect(() => {
    const loadAvailableNarratives = async () => {
      if (!project || !organization || !authToken) return;

      try {
        setLoadingNarratives(true);

        const { data } = await api.get(
          "/projects/gpt-narratives",
          {
            params: {
              projectId: project,
              organizationId: organization,
            },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (data?.response?.status_response === "OK") {
          const narratives = data.response.data?.narratives || [];
          setAvailableNarratives(narratives);

          // Preseleccionar la narrativa por defecto (isDefault) cuando no hay una
          // selección válida actual (p. ej. al llegar a la sección sin elegir).
          const currentIsValid =
            selectedNarrative?.id &&
            narratives.some((narrative) => narrative.id === selectedNarrative.id);

          if (!currentIsValid) {
            const defaultNarrative =
              narratives.find((narrative) => narrative.isDefault) ||
              narratives[0] ||
              null;
            setSelectedNarrative(defaultNarrative);
          }
        }
      } catch (err) {
        console.error("Error al cargar narrativas disponibles:", err);
        showAlert("Error al cargar las narrativas disponibles", "error");
      } finally {
        setLoadingNarratives(false);
      }
    };

    loadAvailableNarratives();
  }, [project, organization, authToken]);

  const handleSelectedNarrativeChange = (event) => {
    const narrativeId = event.target.value;
    const narrative = availableNarratives.find((item) => item.id === narrativeId);

    setSelectedNarrative(narrative || null);
  };

  const handleOpenNarrativeDialog = () => {
    if (!selectedNarrative) return;

    setSelectedNarrativeForm({
      name: selectedNarrative.name || "",
      description: selectedNarrative.description || "",
      content: selectedNarrative.content || "",
    });

    setOpenNarrativeDialog(true);
  };

  const handleCloseNarrativeDialog = () => {
    setOpenNarrativeDialog(false);
    setSelectedNarrativeForm({
      name: "",
      description: "",
      content: "",
    });
  };

  const handleSelectedNarrativeFormChange = (field, value) => {
    if (field === "content" && value.length > SELECTED_NARRATIVE_MAX_LENGTH) {
      return;
    }

    setSelectedNarrativeForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSelectedNarrativeEdit = () => {
    if (!selectedNarrative) return;

    if (!selectedNarrativeForm.content.trim()) {
      showAlert("La narrativa no puede quedar vacía", "error");
      return;
    }

    setSelectedNarrative((prev) => ({
      ...prev,
      name: selectedNarrativeForm.name.trim() || prev.name,
      description: selectedNarrativeForm.description.trim(),
      content: selectedNarrativeForm.content.trim(),
    }));

    handleCloseNarrativeDialog();
  };

  // Reset stepper cuando se crea nuevo reporte
  useEffect(() => {
    if (!reportId) {
      setActiveStep(0);
      setVisitedSteps(new Set());
    }
  }, [reportId]);

  // Funciones para configuración de pasos dinámicos
  const openStepConfig = (stepIndex) => {
    const step = dynamicSteps[stepIndex];
    setEditingStepIndex(stepIndex);
    setStepConfig({
      title: step.title,
      description: step.description,
      gptInstructions: step.gptInstructions
    });
    setOpenConfigDialog(true);
  };

  const saveStepConfig = () => {
    if (editingStepIndex !== null) {
      const updatedSteps = [...dynamicSteps];
      updatedSteps[editingStepIndex] = {
        ...updatedSteps[editingStepIndex],
        ...stepConfig
      };
      handleDynamicStepsChange(updatedSteps);
    }
    setOpenConfigDialog(false);
    setEditingStepIndex(null);
  };

  // Funciones para el modal de crear cargo
  const handleOpenPositionsModal = () => {
    setOpenPositionsModal(true);
  };

  const handleClosePositionsModal = () => {
    setOpenPositionsModal(false);
  };

  const handlePositionCreated = async (newPosition) => {
    // Actualizar contador para forzar refresh interno
    setRefreshPositions(prev => prev + 1);

    // Cerrar el modal primero
    setOpenPositionsModal(false);

    // Llamar a la función del padre para recargar positions desde el servidor
    if (onRefreshPositions) {
      try {
        await onRefreshPositions();
      } catch (error) {
        console.error('Error al recargar positions:', error);
      }
    }

    // Seleccionar automáticamente el nuevo cargo después de un delay más largo
    setTimeout(() => {
      console.log('Seleccionando cargo automáticamente:', newPosition);
      onPositionChange(null, newPosition);
    }, 500);
  };


  // Función para verificar si se puede avanzar al siguiente paso
  const canAdvanceToNextStep = useCallback((stepIndex) => {
    if (stepIndex === 0) {
      const hasCandidateData =
        candidateData.applicant !== null ||
        (candidateData.name.trim() !== "" &&
          candidateData.email.trim() !== "" &&
          isValidEmail(candidateData.email.trim()));

      const hasFilesOrContext =
        localFiles[0]?.length > 0 ||
        (contexts[0] && contexts[0].trim() !== "");

      return hasCandidateData && hasFilesOrContext;
    }

    if (stepIndex === 1) {
      return selectedPosition !== null;
    }

    // Para otros pasos
    return (
      localFiles[stepIndex]?.length > 0 ||
      (contexts[stepIndex] && contexts[stepIndex].trim() !== "") ||
      skipped[stepIndex]
    );
  }, [candidateData, localFiles, contexts, skipped, selectedPosition]);

  // Manejo de archivos
  const handleFiles = (step, filesList) => {
    const valid = Array.from(filesList).filter((f) =>
      /\.(pdf|docx?|xlsx?|pptx?)$/i.test(f.name)
    );
    onFilesChange(step, valid);
  };

  const handleInput = (step) => (e) => handleFiles(step, e.target.files);

  const handleDrop = (step) => (e) => {
    e.preventDefault();
    setDraggingStep(null);
    handleFiles(step, e.dataTransfer.files);
  };

  const handleDragOver = (step) => (e) => {
    e.preventDefault();
    setDraggingStep(step);
  };

  const handleDragLeave = () => setDraggingStep(null);

  const removeLocalFile = async (step, idx) => {
    const fileToDelete = localFiles[step][idx];
    if (reportId && fileToDelete?.name) {
      await api.patch(
        `/reports/${reportId}/remove-file`,
        { fileName: fileToDelete.name },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
    }

    const newFiles = [...localFiles];
    newFiles[step] = newFiles[step].filter((_, i) => i !== idx);
    onFilesChange(step, newFiles[step], true); // true indica reemplazo completo
  };

  const toggleSkip = (step) => {
    const newSkipped = [...skipped];
    newSkipped[step] = !newSkipped[step];
    onSkippedChange(newSkipped);
  };

  // Navegación del stepper
  const handleNext = () => {
    const totalSteps = allSteps.length;
    if (activeStep < totalSteps - 1 && canAdvanceToNextStep(activeStep)) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      // Marcar el paso actual como visitado cuando avanzamos
      setVisitedSteps(prev => new Set([...prev, activeStep]));
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Función para verificar si el candidato tiene CV disponible
  const fetchApplicantCv = useCallback(async () => {
    setLoadingCv(true);
    try {
      console.log('🔍 Verificando si el candidato tiene CV:', {
        applicantId: candidateData.applicant.id,
        cvUrl: candidateData.applicant.cvUrl
      });

      if (candidateData.applicant.cvUrl) {
        console.log('✅ CV encontrado:', candidateData.applicant.cvUrl);
        setAvailableCv({
          cvUrl: candidateData.applicant.cvUrl,
          applicantName: candidateData.applicant.name
        });
      } else {
        console.log('❌ El candidato no tiene CV cargado');
        setAvailableCv(null);
      }
    } catch (error) {
      console.error('❌ Error verificando CV del candidato:', error);
      setAvailableCv(null);
    } finally {
      setLoadingCv(false);
    }
  }, [candidateData.applicant]);

  // Función para cargar el CV automáticamente
  const handleLoadCv = useCallback(async () => {
    if (!availableCv?.cvUrl) return;

    try {
      // Crear un objeto "file" simulado con la URL del CV
      const cvFile = {
        name: `CV_${candidateData.applicant.name}.pdf`,
        type: 'application/pdf',
        url: availableCv.cvUrl,
        isCvFromApplicant: true
      };

      // Agregar a localFiles del paso 0 (índice 0)
      onFilesChange(0, [cvFile], false);

      showAlert('CV del candidato cargado automáticamente', 'success');
      setAvailableCv(null); // Ocultar el alert
    } catch (error) {
      console.error('Error cargando CV:', error);
      showAlert('Error al cargar el CV del candidato', 'error');
    }
  }, [availableCv, candidateData.applicant?.name, onFilesChange, showAlert]);

  // useEffect para verificar CV cuando se selecciona un candidato en el paso 0
  useEffect(() => {
    // Solo verificar si estamos CREANDO un reporte nuevo Y no hay archivos ya cargados
    if (!reportId &&
      activeStep === 0 &&
      candidateData.applicant?.id &&
      candidateData.applicant?.cvUrl &&
      (!localFiles[0] || localFiles[0].length === 0)) {
      console.log('✅ Verificando CV del candidato...');
      fetchApplicantCv();
    }
  }, [reportId, activeStep, candidateData.applicant?.id, candidateData.applicant?.cvUrl, localFiles, fetchApplicantCv]);

  // Renderizar paso de posición
  const renderPositionStep = () => {
    return (
      <Box>
        {/* Header con icono y botón de nuevo cargo */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <WorkIcon sx={{ mr: 1 }} />
            {t('reports.layout.selectPosition')}
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenPositionsModal}
            disabled={sending}
            startIcon={<AddIcon />}
            size="small"
          >
            {t('positions.newPosition')}
          </Button>
        </Box>

        <Autocomplete
          value={selectedPosition}
          onChange={onPositionChange}
          options={positions}
          getOptionKey={(option) => option.uid ?? option.id}
          filterOptions={(options) => filterPositionsByName(options, positionSearchText)}
          onInputChange={(event, newInputValue) => setPositionSearchText(newInputValue)}
          isOptionEqualToValue={(option, value) =>
            (option.uid || option.id) === (value.uid || value.id)
          }
          getOptionLabel={(option) => option.name || ""}
          renderOption={(props, option) => (
            // key única por uid: con nombres de cargo duplicados, la key por
            // defecto (el nombre) rompe el render de la lista al filtrar.
            <Box component="li" {...props} key={option.uid ?? option.id}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {option.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.industria} •{" "}
                  {option.descripcion
                    ? option.descripcion.substring(0, 100) +
                    (option.descripcion.length > 100 ? "..." : "")
                    : t('noDescription')}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('positions.searchPlaceholder')}
              placeholder={t('positions.searchPlaceholder')}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPositions && <LinearProgress />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          loading={loadingPositions}
          noOptionsText={t('reports.layout.positionsNotFound')}
          disabled={sending}
        />

        {selectedPosition && (
          <PositionCard>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  {t('reports.layout.selectedPosition')}
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                {selectedPosition.name}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={selectedPosition.industria}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
                {selectedPosition.jerarquia && (
                  <Chip
                    label={selectedPosition.jerarquia}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  />
                )}
              </Box>

              {/* Sección compacta con altura máxima */}
              <Box sx={{
                maxHeight: 300,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                backgroundColor: '#fafafa'
              }}>
                {selectedPosition.descripcion && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                      Misión
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {selectedPosition.descripcion}
                    </Typography>
                  </Box>
                )}

                {selectedPosition.responsabilidades && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                      Principales Responsabilidades
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                      {selectedPosition.responsabilidades}
                    </Typography>
                  </Box>
                )}

                {selectedPosition.perfil && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                      Perfil General
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                      {selectedPosition.perfil}
                    </Typography>
                  </Box>
                )}

                {selectedPosition.conocimientosExcluyentes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'error.main' }}>
                      Conocimientos Excluyentes
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                      {selectedPosition.conocimientosExcluyentes}
                    </Typography>
                  </Box>
                )}

                {selectedPosition.conocimientosDeseables && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'success.main' }}>
                      Conocimientos Deseables
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                      {selectedPosition.conocimientosDeseables}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </PositionCard>
        )}
      </Box>
    );
  };

  const renderNarrativeSelection = () => {
    return (
      <Paper
        sx={{
          p: 3,
          mb: 3,
          maxWidth: 760,
          mx: "auto",
          textAlign: "left",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <AutoStoriesIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            Narrativa del informe
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona la narrativa que se aplicará de forma global a todo el informe.
          Puedes editarla para este reporte antes de generar.
        </Typography>

        {loadingNarratives && (
          <Box sx={{ display: "flex", alignItems: "center", py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Cargando narrativas...
            </Typography>
          </Box>
        )}

        {!loadingNarratives && availableNarratives.length === 0 && (
          <Alert severity="warning">
            No hay narrativas disponibles. Crea una narrativa en Configuración antes de generar el informe.
          </Alert>
        )}

        {!loadingNarratives && availableNarratives.length > 0 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FormControl fullWidth>
                <InputLabel>Narrativa</InputLabel>
                <Select
                  value={selectedNarrative?.id || ""}
                  label="Narrativa"
                  onChange={handleSelectedNarrativeChange}
                  disabled={sending || !!activeReport}
                >
                  {availableNarratives.map((narrative) => (
                    <MenuItem key={narrative.id} value={narrative.id}>
                      {narrative.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Editar narrativa para este reporte">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleOpenNarrativeDialog}
                    disabled={!selectedNarrative || sending || !!activeReport}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {selectedNarrative && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                }}
              >
                {selectedNarrative.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selectedNarrative.description}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  {selectedNarrative.content}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  // Renderizar paso final de generación de informe
  const renderReportGenerationStep = () => {
    return (
      <Box sx={{ textAlign: "center" }}>
        {!activeReport && renderNarrativeSelection()}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 3,
            flexWrap: "wrap",
          }}
        >
          {!activeReport ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<DescriptionIcon />}
              onClick={() => onCreateReport(selectedNarrative)}
              disabled={sending || !allDone || !selectedNarrativeHasContent}
              sx={{ px: 4, py: 1.5 }}
            >
              {sending ? t('sending') : t('reports.layout.generateReport')}
            </Button>
          ) : (
            <>
              {gptTask?.status === "error" && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  startIcon={<AutorenewIcon />}
                  onClick={() => onRetryGptTask(gptTask.id)}
                  disabled={gptProcessing}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {t('reports.layout.retryProccesing')}
                </Button>
              )}

              {gptTask?.status === "pendiente" && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => onProcessGptTask(gptTask.id)}
                  disabled={gptProcessing}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {t('reports.layout.startProcessing')}
                </Button>
              )}

              {isInFinalState(activeReport.status) && (
                <>
                  {(activeReport.report_url ||
                    activeReport.word_document_url ||
                    gptTask?.result?.reportHtmlUrl ||
                    gptTask?.result?.reportWordUrl) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "medium",
                            color: "success.main",
                            mb: 2,
                          }}
                        >
                          {t('reports.layout.reportGeneratedSuccess')}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 2,
                            width: "100%",
                            justifyContent: "center",
                          }}
                        >
                          {(activeReport.report_url ||
                            gptTask?.result?.reportHtmlUrl) && (
                              <ActionButton
                                variant="outlined"
                                color="primary"
                                size="large"
                                startIcon={<VisibilityIcon />}
                                onClick={() =>
                                  window.open(
                                    activeReport.report_url ||
                                    gptTask.result.reportHtmlUrl,
                                    "_blank"
                                  )
                                }
                              >
                                {t('reports.layout.showHtmlReport')}
                              </ActionButton>
                            )}

                          {(activeReport.word_document_url ||
                            gptTask?.result?.reportWordUrl) && (
                              <ActionButton
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<FileDownloadIcon />}
                                onClick={() =>
                                  downloadWordDocument(
                                    activeReport.word_document_url ||
                                    gptTask.result.reportWordUrl,
                                    `Informe_${activeReport.id || "Candidato"}.docx`
                                  )
                                }
                              >
                                {t('downloadWord')}
                              </ActionButton>
                            )}
                        </Box>
                      </Box>
                    )}
                </>
              )}
            </>
          )}
        </Box>

        {sending && (
          <Box sx={{ mt: 3, maxWidth: 400, mx: "auto" }}>
            <Typography variant="body1" gutterBottom>
              {t('reports.layout.uploadedPlural')} {uploadedCount} / {totalFiles}
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {gptProcessing && (
          <Box sx={{ mt: 3, maxWidth: 400, mx: "auto" }}>
            <Typography variant="body1" gutterBottom>
              {t('processing')}
            </Typography>
            {isInFinalState(gptTask?.status) ? (
              <LinearProgress
                variant="determinate"
                value={100}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
            ) : (
              <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
            )}
          </Box>
        )}

        {activeReport?.status && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1">
              {t('statusWord')}: <strong>{t(`status.${activeReport.status}`)}</strong>
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Renderizar contenido del paso
  const renderStepContent = (stepIndex) => {
    // Si es el paso final (generación de informe)
    if (stepIndex === totalStepsWithoutFinal) {
      return renderReportGenerationStep();
    }

    // Si es un paso dinámico, mostrar configuración arriba
    const isDynamicStep = stepIndex >= pasos.length && stepIndex < totalStepsWithoutFinal;
    const dynamicStepIndex = isDynamicStep ? stepIndex - pasos.length : null;
    const currentDynamicStep = isDynamicStep ? dynamicSteps[dynamicStepIndex] : null;

    return (
      <>
        {/* Configuración del paso dinámico */}
        {isDynamicStep && currentDynamicStep && (
          <Box sx={{ mb: 3 }}>
            {/* Instrucción específica para pasos dinámicos */}
            <Box sx={{
              p: 2,
              backgroundColor: '#e8f5e8',
              borderRadius: 1,
              border: '1px solid #4caf50',
              mb: 2
            }}>
              <Typography variant="body2">
                {t('reports.dynamicSteps.instruction')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6" color="primary">
                Configuración del Paso
              </Typography>
              <Tooltip title="Configurar paso">
                <IconButton
                  size="small"
                  onClick={() => openStepConfig(dynamicStepIndex)}
                  color="primary"
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {currentDynamicStep.gptInstructions && (
              <Box sx={{
                p: 2,
                backgroundColor: '#e3f2fd',
                borderRadius: 1,
                border: '1px solid #bbdefb',
                mb: 2
              }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('reports.dynamicSteps.gptInstructions')}
                </Typography>
                <Typography variant="body2">
                  {currentDynamicStep.gptInstructions}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {stepIndex === 0 ? (
          <>
            <CandidateSearchForm
              authToken={authToken}
              onCandidateChange={onCandidateChange}
              initialName={candidateData.name}
              initialRUT={candidateData.rut}
              initialApplicant={candidateData.applicant}
            />

            {/* Alerta de CV disponible */}
            {!reportId && availableCv && (
              <Box sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e8',
                    border: '2px solid #4caf50',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}>
                        ✅ {t('reports.steps.step1.cvAvailable')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('reports.steps.step1.cvAvailableMessage')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('reports.steps.step1.candidateLabel')} {availableCv.applicantName}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleLoadCv}
                      sx={{ ml: 2 }}
                    >
                      {t('reports.steps.step1.loadAutomatically')}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Loading del CV */}
            {!reportId && loadingCv && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ mr: 2 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #4caf50',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('reports.steps.step1.checkingCv')}
                </Typography>
              </Box>
            )}

            {/* Recomendación para CV */}
            <Box sx={{
              p: 2,
              backgroundColor: '#e8f5e8',
              borderRadius: 1,
              border: '1px solid #4caf50',
              mb: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('reports.steps.step1.recommendation')}
              </Typography>
              <Typography variant="body2">
                {t('reports.steps.step1.recommendationText')}
              </Typography>
            </Box>

            <DragDropArea
              isdragging={(draggingStep === stepIndex).toString()}
              onDrop={handleDrop(stepIndex)}
              onDragOver={handleDragOver(stepIndex)}
              onDragLeave={handleDragLeave}
            >
              <Typography>{t('dragOver')}</Typography>
              <label htmlFor={`upload-${stepIndex}`}>
                <Input
                  id={`upload-${stepIndex}`}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleInput(stepIndex)}
                  disabled={sending}
                />
                <Button
                  variant="outlined"
                  sx={{ mt: 1 }}
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  {t('select')}
                </Button>
              </label>
            </DragDropArea>

            {localFiles[stepIndex]?.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {localFiles[stepIndex].map((file, i) => (
                  <ListItem
                    key={`${file.name}-${i}`}
                    secondaryAction={
                      <Box>
                        {file.url && (
                          <Tooltip title={t('downloadFile')}>
                            <IconButton
                              edge="end"
                              onClick={() => downloadFile(file.url, file.name)}
                              sx={{ mr: 1 }}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('deleteFile')}>
                          <IconButton
                            edge="end"
                            onClick={() => removeLocalFile(stepIndex, i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={
                        file.url ? t('uploadedFile') : t('uploadFilePending')
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('optionalContext')}
              placeholder={t('optionalContextPlaceholder')}
              value={contexts[stepIndex] || ""}
              onChange={(e) => onContextChange(stepIndex, e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        ) : stepIndex === 1 ? (
          renderPositionStep()
        ) : stepIndex === 2 ? (
          <>
            {/* Alerta de Test DISC disponible */}
            {!reportId && availableDiscTest && (
              <Box sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    border: '2px solid #2196f3',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        ✅ Test DISC Disponible
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Se encontró un test DISC completado para este candidato en este cargo.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completado el: {availableDiscTest.completedAt?._seconds
                          ? new Date(availableDiscTest.completedAt._seconds * 1000).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                          : 'Fecha no disponible'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleLoadDiscTest}
                      sx={{ ml: 2 }}
                    >
                      Cargar Automáticamente
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Loading del test DISC */}
            {!reportId && loadingDiscTest && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ mr: 2 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Buscando test DISC del candidato...
                </Typography>
              </Box>
            )}

            <DragDropArea
              isdragging={(draggingStep === stepIndex).toString()}
              onDrop={handleDrop(stepIndex)}
              onDragOver={handleDragOver(stepIndex)}
              onDragLeave={handleDragLeave}
            >
              <Typography>{t('dragOver')}</Typography>
              <label htmlFor={`upload-${stepIndex}`}>
                <Input
                  id={`upload-${stepIndex}`}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleInput(stepIndex)}
                  disabled={sending}
                />
                <Button
                  variant="outlined"
                  sx={{ mt: 1 }}
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  {t('select')}
                </Button>
              </label>
            </DragDropArea>

            {localFiles[stepIndex]?.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {localFiles[stepIndex].map((file, i) => (
                  <ListItem
                    key={`${file.name}-${i}`}
                    secondaryAction={
                      <Box>
                        {file.url && (
                          <Tooltip title={t('downloadFile')}>
                            <IconButton
                              edge="end"
                              onClick={() => downloadFile(file.url, file.name)}
                              sx={{ mr: 1 }}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('deleteFile')}>
                          <IconButton
                            edge="end"
                            onClick={() => removeLocalFile(stepIndex, i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={
                        file.url ? t('uploadedFile') : t('uploadFilePending')
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('optionalContext')}
              placeholder={t('optionalContextPlaceholder')}
              value={contexts[stepIndex] || ""}
              onChange={(e) => onContextChange(stepIndex, e.target.value)}
              sx={{ mt: 2 }}
            />

            <Box sx={{ textAlign: "right", mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toggleSkip(stepIndex)}
              >
                {skipped[stepIndex] ? t('unmarkNotApply') : t('markNotApply')}
              </Button>
            </Box>
          </>
        ) : stepIndex === 3 ? (
          <>
            {/* Alerta de Tests de Razonamiento disponibles */}
            {!reportId && availableReasoningTests.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e9',
                    border: '2px solid #4caf50',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}>
                        ✅ {t('reports.layout.reasoningTestsAvailableCount', { count: availableReasoningTests.length })}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('reports.layout.reasoningTestsFoundMessage')}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {availableReasoningTests.map((test, index) => {
                          return (
                            <Typography key={index} variant="caption" display="block" color="text.secondary">
                              • {t(`reports.layout.reasoningTestTypes.${test.testType}`)} - {t('reports.layout.reasoningTestCompleted')} {test.completedAt?._seconds
                                ? new Date(test.completedAt._seconds * 1000).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })
                                : t('reports.layout.reasoningTestDateNotAvailable')}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleLoadReasoningTests}
                      sx={{ ml: 2 }}
                    >
                      {t('reports.layout.loadAllReasoningTests')}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Loading de tests de razonamiento */}
            {!reportId && loadingReasoningTests && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ mr: 2 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #4caf50',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('reports.layout.searchingReasoningTests')}
                </Typography>
              </Box>
            )}

            <DragDropArea
              isdragging={(draggingStep === stepIndex).toString()}
              onDrop={handleDrop(stepIndex)}
              onDragOver={handleDragOver(stepIndex)}
              onDragLeave={handleDragLeave}
            >
              <Typography>{t('dragOver')}</Typography>
              <label htmlFor={`upload-${stepIndex}`}>
                <Input
                  id={`upload-${stepIndex}`}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleInput(stepIndex)}
                  disabled={sending}
                />
                <Button
                  variant="outlined"
                  sx={{ mt: 1 }}
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  {t('select')}
                </Button>
              </label>
            </DragDropArea>

            {localFiles[stepIndex]?.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {localFiles[stepIndex].map((file, i) => (
                  <ListItem
                    key={`${file.name}-${i}`}
                    secondaryAction={
                      <Box>
                        {file.url && (
                          <Tooltip title={t('downloadFile')}>
                            <IconButton
                              edge="end"
                              onClick={() => downloadFile(file.url, file.name)}
                              sx={{ mr: 1 }}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('deleteFile')}>
                          <IconButton
                            edge="end"
                            onClick={() => removeLocalFile(stepIndex, i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={
                        file.url
                          ? t('uploadedFile')
                          : t('uploadFilePending')
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('optionalContext')}
              placeholder={t('optionalContextPlaceholder')}
              value={contexts[stepIndex] || ""}
              onChange={(e) => onContextChange(stepIndex, e.target.value)}
              sx={{ mt: 2 }}
            />

            <Box sx={{ textAlign: "right", mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toggleSkip(stepIndex)}
              >
                {skipped[stepIndex] ? t('unmarkNotApply') : t('markNotApply')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Recomendación para paso 5 (Entrevista Técnica) */}
            {stepIndex === 4 && (
              <Box sx={{
                p: 2,
                backgroundColor: '#e8f5e8',
                borderRadius: 1,
                border: '1px solid #4caf50',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {t('reports.steps.step5.recommendation')}
                </Typography>
                <Typography variant="body2">
                  {t('reports.steps.step5.recommendationText')}
                </Typography>
              </Box>
            )}

            {/* Alerta de Entrevista disponible */}
            {!reportId && stepIndex === 4 && availableInterview && (
              <Box sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    border: '2px solid #2196f3',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        ✅ Entrevista Disponible
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Se encontró una entrevista completada para este candidato.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completado el: {availableInterview.completedAt?._seconds
                          ? new Date(availableInterview.completedAt._seconds * 1000).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                          : 'Fecha no disponible'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleLoadInterview}
                      sx={{ ml: 2 }}
                    >
                      Cargar Automáticamente
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Loading de la entrevista */}
            {!reportId && stepIndex === 4 && loadingInterview && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Box sx={{ mr: 2 }}>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Buscando entrevista del candidato...
                </Typography>
              </Box>
            )}

            <DragDropArea
              isdragging={(draggingStep === stepIndex).toString()}
              onDrop={handleDrop(stepIndex)}
              onDragOver={handleDragOver(stepIndex)}
              onDragLeave={handleDragLeave}
            >
              <Typography>{t('dragOver')}</Typography>
              <label htmlFor={`upload-${stepIndex}`}>
                <Input
                  id={`upload-${stepIndex}`}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleInput(stepIndex)}
                  disabled={sending}
                />
                <Button
                  variant="outlined"
                  sx={{ mt: 1 }}
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  {t('select')}
                </Button>
              </label>
            </DragDropArea>

            {localFiles[stepIndex]?.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {localFiles[stepIndex].map((file, i) => (
                  <ListItem
                    key={`${file.name}-${i}`}
                    secondaryAction={
                      <Box>
                        {file.url && (
                          <Tooltip title={t('downloadFile')}>
                            <IconButton
                              edge="end"
                              onClick={() => downloadFile(file.url, file.name)}
                              sx={{ mr: 1 }}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('deleteFile')}>
                          <IconButton
                            edge="end"
                            onClick={() => removeLocalFile(stepIndex, i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={
                        file.url ? t('uploadedFile') : t('uploadFilePending')
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('optionalContext')}
              placeholder={t('optionalContextPlaceholder')}
              value={contexts[stepIndex] || ""}
              onChange={(e) => onContextChange(stepIndex, e.target.value)}
              sx={{ mt: 2 }}
            />

            <Box sx={{ textAlign: "right", mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toggleSkip(stepIndex)}
              >
                {skipped[stepIndex] ? t('unmarkNotApply') : t('markNotApply')}
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  // Función para buscar test DISC disponible
  const fetchDiscTest = useCallback(async () => {
    setLoadingDiscTest(true);
    try {
      const positionId = selectedPosition.uid || selectedPosition.id;

      console.log('🔍 Buscando test DISC con:', {
        applicantId: candidateData.applicant.id,
        positionId: positionId,
        projectId: project
      });

      const { data } = await api.get('/disc-tests/by-applicant', {
        params: {
          applicantId: candidateData.applicant.id,
          positionId: positionId,
          projectId: project
        },
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('📥 Respuesta del servidor:', data);

      if (data.errorCode === 0 &&
        data.response?.data?.length > 0 &&
        data.response.data[0].reportUrl) {
        console.log('✅ Test DISC encontrado:', data.response.data[0]);
        setAvailableDiscTest(data.response.data[0]);
      } else {
        console.log('❌ No se encontró test DISC evaluado');
        setAvailableDiscTest(null);
      }
    } catch (error) {
      console.error('❌ Error buscando test DISC:', error);
      setAvailableDiscTest(null);
    } finally {
      setLoadingDiscTest(false);
    }
  }, [candidateData.applicant, selectedPosition, project, authToken]);

  // Función para cargar el test DISC automáticamente
  const handleLoadDiscTest = useCallback(async () => {
    if (!availableDiscTest?.reportUrl) return;

    try {
      // Crear un objeto "file" simulado con la URL del PDF
      const discFile = {
        name: `Test_DISC_${candidateData.applicant.name}.pdf`,
        type: 'application/pdf',
        url: availableDiscTest.reportUrl,
        isDiscTest: true,
        completedAt: availableDiscTest.completedAt
      };

      // Agregar a localFiles del paso 2 (índice 2)
      onFilesChange(2, [discFile], false);

      showAlert('Test DISC cargado automáticamente', 'success');
      setAvailableDiscTest(null); // Ocultar el alert
    } catch (error) {
      console.error('Error cargando test DISC:', error);
      showAlert('Error al cargar el test DISC', 'error');
    }
  }, [availableDiscTest, candidateData.applicant?.name, onFilesChange, showAlert]);

  // useEffect para buscar test DISC cuando llegamos al paso 2
  useEffect(() => {
    // Solo buscar si estamos CREANDO un reporte nuevo Y no hay archivos ya cargados
    if (!reportId &&
      activeStep === 2 &&
      candidateData.applicant?.id &&
      selectedPosition?.uid &&
      (!localFiles[2] || localFiles[2].length === 0)) {  // ← NUEVA CONDICIÓN
      console.log('✅ Todas las condiciones cumplidas, buscando test DISC...');
      fetchDiscTest();
    }
  }, [reportId, activeStep, candidateData.applicant?.id, selectedPosition?.uid, project, localFiles, fetchDiscTest]);

  // Función para buscar tests de razonamiento disponibles
  const fetchReasoningTests = useCallback(async () => {
    setLoadingReasoningTests(true);
    try {
      console.log('🔍 Buscando tests de razonamiento con:', {
        applicantId: candidateData.applicant.id,
        projectId: project
      });

      const { data } = await api.get('/reasoning-tests/latest-by-applicant', {
        params: {
          applicantId: candidateData.applicant.id,
          projectId: project
        },
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('📥 Respuesta del servidor:', data);

      if (data.errorCode === 0 &&
        data.response?.data?.length > 0) {
        // Filtrar solo tests con reportUrl
        const testsWithReport = data.response.data.filter(test =>
          test.reportUrl
        );
        console.log('✅ Tests de razonamiento encontrados:', testsWithReport.length);
        setAvailableReasoningTests(testsWithReport);
      } else {
        console.log('❌ No se encontraron tests de razonamiento completados');
        setAvailableReasoningTests([]);
      }
    } catch (error) {
      console.error('❌ Error buscando tests de razonamiento:', error);
      setAvailableReasoningTests([]);
    } finally {
      setLoadingReasoningTests(false);
    }
  }, [candidateData.applicant, project, authToken]);


  // Función para cargar los tests de razonamiento automáticamente
  const handleLoadReasoningTests = useCallback(async () => {
    if (!availableReasoningTests || availableReasoningTests.length === 0) return;

    try {
      // Crear objetos "file" simulados para cada test
      const reasoningFiles = availableReasoningTests.map(test => {
        const testTypeNames = {
          wordAssociation: 'Asociación_de_Palabras',
          logicalProcess: 'Proceso_Lógico',
          numericalReasoning: 'Razonamiento_Numérico',
          socialContext: 'Contexto_Social'
        };

        const testTypeName = testTypeNames[test.testType] || test.testType;

        return {
          name: `Test_${testTypeName}_${candidateData.applicant.name}.pdf`,
          type: 'application/pdf',
          url: test.reportUrl,
          isReasoningTest: true,
          testType: test.testType,
          completedAt: test.completedAt
        };
      });

      // Agregar a localFiles del paso 3 (índice 3)
      onFilesChange(3, reasoningFiles, false);

      showAlert(`${reasoningFiles.length} test(s) de razonamiento cargado(s) automáticamente`, 'success');
      setAvailableReasoningTests([]); // Ocultar el alert
    } catch (error) {
      console.error('Error cargando tests de razonamiento:', error);
      showAlert('Error al cargar los tests de razonamiento', 'error');
    }
  }, [availableReasoningTests, candidateData.applicant?.name, onFilesChange, showAlert]);

  useEffect(() => {
    // Solo buscar si NO hay archivos ya cargados en el paso 4
    if (!reportId &&
      activeStep === 3 &&
      candidateData.applicant?.id &&
      project &&
      (!localFiles[3] || localFiles[3].length === 0)) {
      fetchReasoningTests();
    }
  }, [reportId, activeStep, candidateData.applicant?.id, project, localFiles, fetchReasoningTests]);

  // Función para buscar entrevista disponible
  const fetchInterview = useCallback(async () => {
    setLoadingInterview(true);
    try {
      const { data } = await api.get(`/applicants/${candidateData.applicant.id}/interviews`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const list = data.response?.data || data.data || [];
      const completed = Array.isArray(list)
        ? list.find((i) => i.status === 'completed' && i.transcriptTxtUrl)
        : null;

      if (completed) {
        setAvailableInterview(completed);
      } else {
        setAvailableInterview(null);
      }
    } catch (error) {
      console.error('❌ Error buscando entrevista:', error);
      setAvailableInterview(null);
    } finally {
      setLoadingInterview(false);
    }
  }, [candidateData.applicant, authToken]);

  // Función para cargar la entrevista automáticamente
  const handleLoadInterview = useCallback(async () => {
    if (!availableInterview?.transcriptTxtUrl) return;

    try {
      // Crear un objeto "file" simulado con la URL del transcript
      const interviewFile = {
        name: `Entrevista_${candidateData.applicant.name}.txt`,
        type: 'application/pdf',
        url: availableInterview.transcriptTxtUrl,
        isInterview: true,
        completedAt: availableInterview.completedAt
      };

      // Agregar a localFiles del paso 5 (índice 4)
      onFilesChange(4, [interviewFile], false);

      showAlert('Entrevista cargada automáticamente', 'success');
      setAvailableInterview(null); // Ocultar el alert
    } catch (error) {
      console.error('Error cargando entrevista:', error);
      showAlert('Error al cargar la entrevista', 'error');
    }
  }, [availableInterview, candidateData.applicant?.name, onFilesChange, showAlert]);

  // useEffect para buscar entrevista cuando llegamos al paso 5 (índice 4)
  useEffect(() => {
    // Solo buscar si NO hay archivos ya cargados en el paso 5
    if (!reportId &&
      activeStep === 4 &&
      candidateData.applicant?.id &&
      (!localFiles[4] || localFiles[4].length === 0)) {
      fetchInterview();
    }
  }, [reportId, activeStep, candidateData.applicant?.id, localFiles, fetchInterview]);

  return (
    <>
      <Paper elevation={2} sx={{ mb: 3 }}>
        {/* Navegación horizontal de pasos */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={1}>
            {allSteps.map((step, index) => {
              const isFinalStep = index === totalStepsWithoutFinal;

              // Si es un paso dinámico
              const isDynamicStep = index >= pasos.length && index < totalStepsWithoutFinal;

              return (
                <Grid item key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 2,
                      minWidth: 120,
                      backgroundColor: activeStep === index ? 'grey.200' : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isStepComplete(index)
                          ? 'success.main'
                          : activeStep === index
                            ? 'primary.main'
                            : 'grey.300',
                        color: 'white',
                        mb: 1,
                      }}
                    >
                      {isStepComplete(index) ? (
                        <CheckCircleOutlineIcon />
                      ) : isFinalStep ? (
                        <DescriptionIcon />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {index + 1}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        fontWeight: activeStep === index ? 'bold' : 'normal',
                        color: activeStep === index ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {step.title}
                    </Typography>
                    {!isFinalStep && skipped[index] && (
                      <Chip
                        label="No Aplica"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}

            {/* Botón para agregar pasos dinámicos */}
            {dynamicSteps.length < 2 && activeStep < totalStepsWithoutFinal && !isReportCompleted && (
              <Grid item>
                <Box
                  onClick={() => {
                    const newStep = {
                      title: `Paso Adicional ${dynamicSteps.length + 1}`,
                      description: 'Paso personalizado agregado por el usuario'
                    };
                    handleDynamicStepsChange([...dynamicSteps, newStep]);
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    cursor: 'pointer',
                    borderRadius: 2,
                    minWidth: 120,
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'grey.50',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.300',
                      color: 'white',
                      mb: 1,
                    }}
                  >
                    <AddIcon />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    Agregar Paso
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Contenido del paso actual */}
        <Box sx={{ p: 3, minHeight: 400 }}>
          <Typography variant="h5" gutterBottom>
            {allSteps[activeStep]?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            {allSteps[activeStep]?.description}
          </Typography>

          {renderStepContent(activeStep)}
        </Box>

        {/* Navegación inferior */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBeforeIcon />}
          >
            {t('previous')}
          </Button>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Botón para eliminar paso dinámico */}
            {activeStep >= pasos.length && activeStep < totalStepsWithoutFinal && dynamicSteps.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  const newSteps = [...dynamicSteps];
                  const indexToRemove = activeStep - pasos.length;
                  newSteps.splice(indexToRemove, 1);
                  handleDynamicStepsChange(newSteps);
                  if (activeStep >= pasos.length + newSteps.length) {
                    setActiveStep(Math.max(0, pasos.length + newSteps.length - 1));
                  }
                }}
              >
                Eliminar Paso
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NavigateNextIcon />}
              disabled={activeStep === allSteps.length - 1 || !canAdvanceToNextStep(activeStep)}
            >
              {activeStep === allSteps.length - 2 ? t('reports.layout.generateReport') : t('next')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Diálogo de Configuración de Pasos Dinámicos */}
      <Dialog open={openConfigDialog} onClose={() => setOpenConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('reports.dynamicSteps.config')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('title')}
              value={stepConfig.title}
              onChange={(e) => setStepConfig({ ...stepConfig, title: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label={t('description')}
              value={stepConfig.description}
              onChange={(e) => setStepConfig({ ...stepConfig, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />

            <TextField
              label={t('reports.dynamicSteps.gptInstructions')}
              value={stepConfig.gptInstructions}
              onChange={(e) => setStepConfig({ ...stepConfig, gptInstructions: e.target.value })}
              fullWidth
              multiline
              rows={4}
              placeholder={t('reports.dynamicSteps.dynamicStepInstructionPlaceholder')}
              helperText={t('reports.dynamicSteps.dynamicStepInstructionHelper')}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfigDialog(false)}>
            {t('cancel')}
          </Button>
          <Button
            onClick={saveStepConfig}
            variant="contained"
            disabled={!stepConfig.title || !stepConfig.description || !stepConfig.gptInstructions}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNarrativeDialog}
        onClose={handleCloseNarrativeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Editar narrativa para este reporte
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta edición solo se usará para este informe. No modifica la narrativa guardada en configuración.
          </Alert>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Nombre"
              value={selectedNarrativeForm.name}
              onChange={(e) =>
                handleSelectedNarrativeFormChange("name", e.target.value)
              }
              fullWidth
              disabled
            />

            <TextField
              label="Descripción"
              value={selectedNarrativeForm.description}
              onChange={(e) =>
                handleSelectedNarrativeFormChange("description", e.target.value)
              }
              fullWidth
              disabled
            />

            <TextField
              label="Contenido de la narrativa"
              value={selectedNarrativeForm.content}
              onChange={(e) =>
                handleSelectedNarrativeFormChange("content", e.target.value)
              }
              fullWidth
              multiline
              minRows={6}
              inputProps={{ maxLength: SELECTED_NARRATIVE_MAX_LENGTH }}
              helperText={`${selectedNarrativeForm.content.length}/${SELECTED_NARRATIVE_MAX_LENGTH}`}
              required
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseNarrativeDialog}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSaveSelectedNarrativeEdit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!selectedNarrativeForm.content.trim()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para crear nuevo cargo */}
      <PositionsModal
        open={openPositionsModal}
        onClose={handleClosePositionsModal}
        mode="create"
        selectedPosition={null}
        onSave={handlePositionCreated}
        authToken={authToken}
        showAlert={showAlert}
        refreshKey={refreshPositions}
        project={project}
      />
    </>
  );
}